import { createContext, useContext, useMemo } from "react";

import { useWatch } from "react-hook-form";

import { useFlows } from "src/api/queries";
import { useCaseQueues } from "src/cases/queries/queues";
import type { DeserializedCaseQueue } from "src/cases/queues/types";
import type { StepId } from "src/cases/templates/formSteps";
import type {
  CasePropertiesValueForm,
  CaseTemplateFormValues,
  CaseTemplateWorkflowRuleFormValue,
} from "src/cases/templates/formUtils/types";
import type { EventConfigEnriched } from "src/clients/features-control";
import type { EntitySchemaResourcePage } from "src/entities/queries";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { useWorkspaceContext } from "src/router/routerContextHooks";

const InvalidReferenceContext = createContext<Set<StepId>>(new Set());

export const useInvalidReferenceSteps = (): Set<StepId> =>
  useContext(InvalidReferenceContext);

const hasInvalidPropertyReferences = (
  properties: CasePropertiesValueForm[] | undefined,
  entitySchemas: EntitySchemaResourcePage | undefined,
  eventSchemas: EventConfigEnriched[] | undefined,
): boolean =>
  properties?.some((property) => {
    if (property.type === "entity_ref" && "entity_type" in property) {
      return !entitySchemas?.entities.some(
        (s) => s._id === property.entity_type,
      );
    }
    if (property.type === "event_ref" && "event_type" in property) {
      return !eventSchemas?.some((s) => s.event_type === property.event_type);
    }
    return false;
  }) ?? false;

const hasInvalidQueueReferences = (
  workflowRules: CaseTemplateWorkflowRuleFormValue | undefined,
  caseQueues: DeserializedCaseQueue[] | undefined,
): boolean => {
  if (!workflowRules) return false;

  const allAutomations = Object.values(workflowRules).flatMap(
    (rule) => rule.actions?.flatMap((action) => action.automation ?? []) ?? [],
  );

  return allAutomations.some(
    (auto) =>
      auto.field === "queue_id" &&
      auto.value &&
      !caseQueues?.some((q) => q.id === auto.value),
  );
};

type InvalidReferenceProviderProps = {
  children: React.ReactNode;
};

/**
 * Provides a set of step IDs that contain invalid references (deleted flows,
 * entity/event types, or queue IDs). Computed synchronously via useMemo so
 * the sidebar never flickers when navigating between steps.
 *
 * Must be mounted inside FormProvider.
 */
export const InvalidReferenceProvider: React.FC<
  InvalidReferenceProviderProps
> = ({ children }) => {
  const { workspace } = useWorkspaceContext();

  const caseProperties = useWatch<
    CaseTemplateFormValues,
    "attributes.case_properties"
  >({ name: "attributes.case_properties" });

  const caseActions = useWatch<
    CaseTemplateFormValues,
    "attributes.case_actions"
  >({ name: "attributes.case_actions" });

  const caseCreationFlowDetails = useWatch<
    CaseTemplateFormValues,
    "attributes.case_creation_flow_details"
  >({ name: "attributes.case_creation_flow_details" });

  const workflowRules = useWatch<
    CaseTemplateFormValues,
    "attributes.workflow_rules"
  >({ name: "attributes.workflow_rules" });

  // Skip invalid reference checks while data is loading to avoid false-positive
  // error icons flashing on page refresh. When a query errors, we also skip
  // that check to avoid false positives.
  const { data: entitySchemas, isError: isEntitySchemasError } =
    useEntitySchemas({
      baseUrl: workspace.base_url!,
    });
  const { data: eventSchemas, isError: isEventSchemasError } =
    useEventSchemas();
  const { data: flows, isError: isFlowsError } = useFlows({
    workspaceId: workspace.id,
    includedTypes: ["case_flow"],
  });
  const { data: caseQueues, isError: isCaseQueuesError } = useCaseQueues();

  const invalidSteps = useMemo(() => {
    const result = new Set<StepId>();

    if (
      !isEntitySchemasError &&
      !isEventSchemasError &&
      entitySchemas !== undefined &&
      eventSchemas !== undefined &&
      hasInvalidPropertyReferences(caseProperties, entitySchemas, eventSchemas)
    ) {
      result.add("define-case-properties");
    }

    if (!isFlowsError && flows !== undefined) {
      const hasInvalidCreationFlow =
        caseCreationFlowDetails?.flow_slug &&
        !flows.some((f) => f.slug === caseCreationFlowDetails.flow_slug);

      const hasInvalidActionFlow = caseActions?.some(
        (action) =>
          action.flow?.flow_id &&
          !flows.some((f) => f.id === action.flow?.flow_id),
      );

      if (hasInvalidCreationFlow) {
        result.add("select-decision-flow");
      }
      if (hasInvalidActionFlow) {
        result.add("configure-case-actions");
      }
    }

    if (
      !isCaseQueuesError &&
      caseQueues !== undefined &&
      hasInvalidQueueReferences(workflowRules, caseQueues)
    ) {
      result.add("define-workflow-rules");
    }

    return result;
  }, [
    caseProperties,
    entitySchemas,
    eventSchemas,
    isEntitySchemasError,
    isEventSchemasError,
    caseCreationFlowDetails,
    caseActions,
    isFlowsError,
    flows,
    workflowRules,
    caseQueues,
    isCaseQueuesError,
  ]);

  return (
    <InvalidReferenceContext.Provider value={invalidSteps}>
      {children}
    </InvalidReferenceContext.Provider>
  );
};
